import axios, {
  AxiosResponse,
  InternalAxiosRequestConfig
} from "axios";
import * as Sentry from "@sentry/react";

import { HOST_API } from "~src/config";
import { getAuthTokenHeader, getUserId } from "~src/utils/authService"; // Added getUserId import
import { logInfo, logError } from "~src/utils/logger";

// Add missing type definitions
// Extend InternalAxiosRequestConfig to include our custom properties
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      requestId?: string;
      startTime?: number;
      requestKey?: string;
      userId?: string; // Track owner of the request
    };
    __retryCount?: number;
  }
}

// Configuration
const API_TIMEOUT = 30000; // 30 seconds
const RETRY_COUNT = 3;
const RETRY_DELAY = 1000; // Base delay in ms

if (!HOST_API) {
  throw new Error("API URL environment variable not set");
}

// Keep track of loading state for UI
let activeRequests = 0;
const updateLoadingState = (increment: boolean) => {
  activeRequests = Math.max(0, activeRequests + (increment ? 1 : -1));
  // Dispatch event for global loading indicator
  window.dispatchEvent(
    new CustomEvent('api:loading', { detail: { isLoading: activeRequests > 0 } })
  );
};

// Track pending requests for cancellation and deduplication
const pendingRequests = new Map();

// Endpoint-specific timeout configuration
const getTimeoutForEndpoint = (url: string): number => {
  // Heavy endpoints that need more time
  const heavyEndpoints = [
    '/api/discover/brand',
    '/api/search/brand-match',
    '/api/search/shopify/product-search',
    '/api/analytics/facebook/ads/trending',
    '/api/winning-products/best-selling-products',
    '/api/brands/fb-ads',
    '/api/competitor-analytics/fb-ads'
  ];
  
  const isHeavyEndpoint = heavyEndpoints.some(endpoint => 
    url?.includes(endpoint)
  );
  
  return isHeavyEndpoint ? 45000 : API_TIMEOUT; // 45s for heavy, 30s default
};

const axiosInstance = axios.create({
  baseURL: HOST_API,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  }
});

/**
 * Generate a unique request ID and set up authorization
 */
axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Initialize metadata if not exists
  config.metadata = config.metadata || {};

  // Generate a unique request ID
  config.metadata.requestId = `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  config.metadata.startTime = new Date().getTime();

  // Get the current user ID for cache management
  const userId = getUserId();
  config.metadata.userId = userId || undefined;

  // Create a request key for deduplication (if method and URL are available)
  if (config.method && config.url) {
    const requestKey = `${config.method}-${config.url}-${JSON.stringify(config.params || {})}-${userId || 'no-user'}`;
    config.metadata.requestKey = requestKey;
  }

  // Track request for potential cancellation
  if (config.metadata.requestKey) {
    // Cancel previous identical requests
    if (pendingRequests.has(config.metadata.requestKey)) {
      const { controller } = pendingRequests.get(config.metadata.requestKey);
      controller.abort('Newer request made');
    }

    // Create a new abort controller
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingRequests.set(config.metadata.requestKey, { controller });
  }

  // Start loading state
  updateLoadingState(true);

  // Get auth header and only add it for protected endpoints
  // Skip auth header for authentication endpoints
  const isAuthEndpoint = config.url && (
    config.url.includes('api/user/login') || 
    config.url.includes('api/user/register') || 
    config.url.includes('api/user/reset-password') ||
    config.url.includes('api/user/verify-reset-link') ||
    config.url.includes('api/user/confirm-reset-password')
  );

  if (!isAuthEndpoint) {
    const authHeader = getAuthTokenHeader();
    
    // Add authorization header if it exists using correct syntax
    if ('Authorization' in authHeader) {
      config.headers['Authorization'] = authHeader.Authorization;
    }
  }

  // Set endpoint-specific timeout
  config.timeout = getTimeoutForEndpoint(config.url || '');

  // Validate request to prevent 400 errors
  // Check query string length for GET requests
  if (config.method?.toLowerCase() === 'get' && config.url) {
    const fullUrl = `${config.baseURL}${config.url}`;
    const urlWithParams = new URL(fullUrl);
    
    // Add params to URL
    if (config.params) {
      Object.keys(config.params).forEach(key => {
        if (config.params[key] !== undefined && config.params[key] !== null) {
          urlWithParams.searchParams.append(key, config.params[key]);
        }
      });
    }
    
    // Check if query string is too long (common cause of 400 errors)
    if (urlWithParams.search.length > 3500) {
      logError(`Query string too long (${urlWithParams.search.length} chars) for ${config.url}`);
      
      const error = new Error('Query string exceeds maximum allowed length. Consider using POST request or pagination.');
      (error as any).config = config;
      
      window.dispatchEvent(new CustomEvent('api:error', {
        detail: {
          status: 400,
          message: 'Request URL too long. Please refine your search criteria.',
          url: config.url
        }
      }));
      
      return Promise.reject(error);
    }
  }

  return config;
});


/**
 * Response handling including caching and retry logic
 */
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const { config } = response;

    // Validate response type to prevent parser errors
    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('text/html') && !contentType.includes('application/json')) {
      logError(`Received HTML response instead of JSON for ${config.url}`);
      
      // Create a proper error object
      const error = new Error('Invalid response type: expected JSON but received HTML');
      (error as any).response = response;
      (error as any).config = config;
      
      // Dispatch error event
      window.dispatchEvent(new CustomEvent('api:error', {
        detail: {
          status: response.status,
          message: 'Server returned an invalid response format',
          url: config?.url
        }
      }));
      
      return Promise.reject(error);
    }

    // End loading state
    updateLoadingState(false);

    // Performance tracking
    if (config.metadata?.startTime) {
      const endTime = new Date().getTime();
      const duration = endTime - config.metadata.startTime;

      // Log request performance for monitoring
      logInfo(`Request ${config.metadata.requestId} completed in ${duration}ms - ${config.method} ${config.url}`);
    }

    // Clean up pending request tracking
    if (config.metadata?.requestKey) {
      pendingRequests.delete(config.metadata.requestKey);
    }

    return response;
  },
  async (error: any) => { // Use any type for now to avoid TS errors
    // End loading state
    updateLoadingState(false);

    const response = error.response;
    const config = error.config;

    // Clean up pending request tracking
    if (config?.metadata?.requestKey) {
      pendingRequests.delete(config.metadata.requestKey);
    }

    // Skip retry for cancelled requests
    if (axios.isCancel(error)) {
      logInfo(`Request cancelled: ${error.message}`);
      return Promise.reject(error);
    }

    // Log errors for debugging
    if (response) {
      const errorDetails = {
        status: response.status,
        url: config?.url,
        method: config?.method,
        data: JSON.stringify(response.data || {}).substring(0, 200), // Trim long responses
        requestId: config?.metadata?.requestId || 'no-id'
      };

      logError(`API Error: ${JSON.stringify(errorDetails)}`);

      // For 401 errors, dispatch auth event
      if (response.status === 401) {
        logInfo('Authentication failed - user may need to login again');

        // Skip retry for auth endpoints to prevent loops
        const isAuthEndpoint = config?.url && (
          config.url.includes('api/user/login') ||
          config.url.includes('api/user/register') ||
          config.url.includes('api/user/refresh')
        );

        if (!isAuthEndpoint) {
          // Clear auth token to prevent cascading 401s
          localStorage.removeItem('accessToken');
          sessionStorage.removeItem('accessToken');

          // Cancel all pending requests to prevent cascading failures
          cancelAllRequests('Authentication failed');

          // Dispatch auth event only once per session
          const authEventKey = 'auth_error_dispatched';
          if (!sessionStorage.getItem(authEventKey)) {
            sessionStorage.setItem(authEventKey, 'true');
            window.dispatchEvent(new CustomEvent('auth:unauthorized', {
              detail: { url: config?.url }
            }));
          }
        }

        // Don't retry 401 errors
        return Promise.reject(error);
      }

      // Implement retry logic for 5xx server errors and network issues
      if ((response.status >= 500 || response.status === 429) && config && (!config.__retryCount || config.__retryCount < RETRY_COUNT)) {
        config.__retryCount = config.__retryCount || 0;
        config.__retryCount++;

        // Exponential backoff for retries
        const delay = config.__retryCount * RETRY_DELAY;

        // For rate limiting, use the Retry-After header if available
        if (response.status === 429 && response.headers['retry-after']) {
          const retryAfter = Number(response.headers['retry-after']) * 1000;
          if (!isNaN(retryAfter)) {
            await new Promise(resolve => setTimeout(resolve, retryAfter));
            return axiosInstance(config);
          }
        }

        logInfo(`Retrying request (${config.__retryCount}/${RETRY_COUNT}) after ${delay}ms - ${config.method} ${config.url}`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return axiosInstance(config);
      }

      // Dispatch global error event for UI notifications
      window.dispatchEvent(new CustomEvent('api:error', {
        detail: {
          status: response.status,
          message: response.data?.message || 'An error occurred',
          url: config?.url
        }
      }));
    } else if (error.request) {
      // Network error - no response received
      logError(`Network error - no response received: ${config?.method} ${config?.url} (${config?.metadata?.requestId || 'no-id'})`);

      // Retry network errors
      if (config && (!config.__retryCount || config.__retryCount < RETRY_COUNT)) {
        config.__retryCount = config.__retryCount || 0;
        config.__retryCount++;

        const delay = config.__retryCount * RETRY_DELAY;
        logInfo(`Retrying network request (${config.__retryCount}/${RETRY_COUNT}) after ${delay}ms - ${config?.method} ${config?.url}`);

        await new Promise(resolve => setTimeout(resolve, delay));
        return axiosInstance(config);
      }

      // Dispatch global event for network error handling in UI
      window.dispatchEvent(new CustomEvent('api:network_error', {
        detail: {
          message: 'Network error - could not connect to server',
          url: config?.url
        }
      }));
    } else {
      // Request setup error
      const errorMessage = error.message || 'Unknown error';
      logError(`Request configuration error: ${errorMessage} (${config?.metadata?.requestId || 'no-id'})`);
    }

    return Promise.reject(error);
  }
);

/**
 * Utility function to cancel all pending requests
 * Useful when navigating away or logging out
 */
export const cancelAllRequests = (reason = 'Request cancelled by user') => {
  const requestCount = pendingRequests.size;
  pendingRequests.forEach(({ controller }) => {
    controller.abort(reason);
  });
  pendingRequests.clear();
  logInfo(`Cancelled ${requestCount} pending requests`);
};

/**
 * Install a global Sentry error handler that ignores canceled requests
 * This should be called early in the application bootstrap process
 */
export const setupSentryErrorFilter = () => {
  try {
    Sentry.addEventProcessor((event: any) => {
      // Skip processing if event doesn't have exception values
      if (!event?.exception?.values?.length) return event;
      
      // Check if this is a canceled request error
      const isCanceledError = event.exception.values.some((exception: any) => {
        if (!exception) return false;
        
        // Check various patterns that indicate a canceled request
        const isCanceledType = exception && exception.type ? 
          (exception.type === 'CanceledError' || exception.type === 'AbortError') : false;
        const isCanceledMessage = exception && exception.value ? (
          exception.value.includes('canceled') || 
          exception.value.includes('aborted')
        ) : false;
        
        // Check stack traces for specific patterns
        const hasCancelStackTrace = exception.stacktrace?.frames?.some((frame: any) => {
          if (!frame) return false;
          const filename = frame.filename || '';
          const functionName = frame.function || '';
          
          return filename.includes('AbortSignal') || 
                 functionName.includes('throwIfCancellationRequested');
        });
        
        return isCanceledType || isCanceledMessage || hasCancelStackTrace;
      });

      // Don't report canceled errors to Sentry
      if (isCanceledError) {
        return null; // Returning null prevents the event from being sent to Sentry
      }

      return event;
    });

    logInfo('Sentry error filter for canceled requests has been installed');
  } catch (error) {
    logError(`Failed to set up Sentry error filter: ${error}`);
  }
};

/**
 * Creates an abort controller that can be used to cancel requests when a component unmounts
 * Returns both the controller and a cleanup function to be used in useEffect
 * 
 * Example usage:
 * 
 * useEffect(() => {
 *   const { controller, cleanup } = createAbortController();
 *   
 *   async function fetchData() {
 *     try {
 *       const data = await someApiCall(params, { signal: controller.signal });
 *       // handle data
 *     } catch (error) {
 *       if (!controller.signal.aborted) {
 *         // Only handle real errors, not cancellations
 *       }
 *     }
 *   }
 *   
 *   fetchData();
 *   return cleanup;
 * }, [dependencies]);
 */
export const createAbortController = () => {
  const controller = new AbortController();
  return {
    controller,
    cleanup: () => controller.abort('Component unmounted'),
    signal: controller.signal
  };
};

/**
 * Add event listener for logout events to cancel pending requests
 */
window.addEventListener('auth:logout', () => {
  // Cancel all pending requests
  cancelAllRequests('User logged out');
});

/**
 * Add event listeners for navigation events to cancel all pending requests
 */
window.addEventListener('beforeunload', () => {
  cancelAllRequests('Page navigation or reload');
});

// Listen for navigation events in SPAs that use History API
window.addEventListener('popstate', () => {
  cancelAllRequests('Browser navigation');
});

// Only run these enhancements in browser environment
if (typeof window !== 'undefined') {
  // Add a global window error handler to detect and filter out canceled request errors
  window.addEventListener('error', function(event) {
    // Check if this is a canceled request error
    const errorText = event.message || '';
    const errorStack = event.error?.stack || '';
    
    // Common patterns for canceled requests
    const cancelPatterns = [
      'canceled',
      'aborted',
      'CanceledError',
      'AbortError',
      'throwIfCancellationRequested',
      'AbortSignal'
    ];
    
    // Check if this error matches any cancel patterns
    const isCanceledError = cancelPatterns.some(pattern => 
      errorText.includes(pattern) || errorStack.includes(pattern)
    );
    
    if (isCanceledError) {
      // Prevent the error from propagating
      event.preventDefault();
      // Log it for debugging but don't report to error monitoring
      logInfo(`Suppressed canceled request error: ${errorText}`);
      return false;
    }
    
    // Let other errors propagate normally
    return true;
  }, true);
  
  logInfo('Global error handler for canceled requests is now active');
}

/**
 * Export the configured axios instance
 */
export default axiosInstance;